import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { TranslateService } from "@ngx-translate/core";
import { ATTRIBUTION_STORAGE_KEY } from "../../utils/tracking-ids";
import { YardGateLog } from "../models/yard-gate-log";
import {
  PublicHoliday,
  StartBlackout,
  StartCalendarResponse,
} from "../models/start-calendar";

/** Parametry, z którymi AutoPay przekierowuje klienta z powrotem do kreatora. */
export interface AutopayReturnParams {
  ServiceID: string;
  OrderID: string;
  Hash: string;
}

@Injectable({
  providedIn: "root",
})
export class CreatorService {
  private itemSource = new BehaviorSubject<string | null>(
    this.getSessionStorageItem("key"),
  );
  item$ = this.itemSource.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly translate: TranslateService,
  ) {
  }

  setSessionStorageItem(key: string, value: any): void {
    sessionStorage.setItem(key, JSON.stringify(value));
    this.itemSource.next(value);
  }

  getSessionStorageItem(key: string, defaultValue: any = null): any | null {
    const item = sessionStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  }

  clearSessionStorageItem(key: string): void {
    sessionStorage.removeItem(key);
    this.itemSource.next(null);
  }

  /**
   * Czyści koszyk kreatora. Atrybucja wejścia (`gclid`, `fbclid` …) zostaje –
   * dotyczy całej wizyty, także kolejnej rezerwacji w tej samej karcie.
   */
  clearSessionStorage() {
    let attribution: string | null = null;
    try {
      attribution = sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    } catch {
      /* storage niedostępny */
    }
    sessionStorage.clear();
    if (attribution) {
      sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, attribution);
    }
    this.itemSource.next(null);
  }

  getLocations(params: any = {}): Observable<any> {
    return this.http.get<any[]>("/api/locations", {
      params,
      withCredentials: true,
      observe: "response",
    });
  }

  getLocation(id: string, params: any = {}): Observable<any> {
    return this.http.get<any[]>(`/api/locations/${id}`, {
      params,
      withCredentials: true,
      observe: "response",
    });
  }

  startReservation(params: any = {}, body: any = {}): Observable<any> {
    return this.http.post(
      "/api/reservation",
      body,
      {
        params,
        withCredentials: true,
        observe: "response",
      },
    );
  }

  /** Zmiana lokalizacji rezerwacji przed płatnością (powrót do pierwszego kroku). */
  changeReservationLocation(
    reservationId: string,
    locationId: string,
    params: any = {},
  ): Observable<any> {
    return this.http.put(
      `/api/reservation/${reservationId}/location`,
      { locationId },
      {
        params,
        withCredentials: true,
        observe: "response",
      },
    );
  }

  saveReservation(data: any, params: any = {}) {
    return this.http.put(`/api/reservation/${data.reservation.id}`, data, {
      params,
      withCredentials: true,
      observe: "response",
    });
  }

  addToReservation(data: any, params: any = {}) {
    return this.http.post(
      `/api/reservation/${data.reservation.id}/details`,
      data,
      {
        params,
        withCredentials: true,
        observe: "response",
      },
    );
  }

  updateReservationDetails(data: any, params: any = {}) {
    return this.http.patch(
      `/api/reservation/${data.reservation.id}/details`,
      data,
      {
        params,
        withCredentials: true,
        observe: "response",
      },
    );
  }

  deleteFromReservation(data: any) {
    return this.http.delete(
      `/api/reservation/${data.reservation.id}/details/${data.itemId}`,
      {
        withCredentials: true,
        observe: "response",
      },
    );
  }

  /**
   * Zapis formularza kreatora: POST (pierwszy) albo PUT (formularz ma już id).
   * Wszystkie błędy (409, 422, 5xx) trafiają do wywołującego – wcześniejsze
   * połykanie otwierało okno potwierdzenia mimo niezapisanych danych.
   */
  saveForm(formId: string, data: any, params: any = {}) {
    const reservationSession = this.getSessionStorageItem(
      "reservationSession",
      [],
    );
    if (reservationSession.id && !formId) {
      return this.http
        .post(`/api/reservation/${reservationSession.id}/form`, data, {
          params,
          withCredentials: true,
          observe: "response",
        });
    } else if (reservationSession.id && formId) {
      return this.http
        .put(`/api/reservation/${reservationSession.id}/form/${formId}`, data, {
          params,
          withCredentials: true,
          observe: "response",
        });
    }
    return new Observable<Object>();
  }

  /**
   * „Przejdź do płatności”. Błędy trafiają do wywołującego (409 – komunikat
   * konfliktu, inne – ogólny komunikat); wcześniejsze połykanie zostawiało
   * kreator z wiecznym spinnerem.
   */
  finishReservation(formId: number, data: any, params: any = {}) {
    const reservationSession = this.getSessionStorageItem(
      "reservationSession",
      [],
    );
    if (reservationSession.id && formId) {
      return this.http.patch(
        `/api/reservation/${reservationSession.id}/form/${formId}`,
        data,
        {
          params,
          withCredentials: true,
          observe: "response",
        },
      );
    }
    return new Observable<Object>();
  }

  getReservation(id: number) {
    return this.http.get(`/api/reservation/${id}`, {
      params: {
        fields: [
          "id",
          "reservationNumber",
          "typeSubscription",
          "rentalPeriod",
          "totalBruttoPrice",
          "totalCommission",
          "startDate",
          "reservationDate",
          "reservationStatus",
          // Krok „Gotowe" na osi statusu – znacznik wysyłki maila z kodami.
          "accessCodesMailSentAt",
          // Zaksięgowana płatność – warunek `purchase` (pomiar, zadanie 5).
          "paymentConfirmedAt",
          "paymentUrl",
          "reservationDetails",
          "location.name",
          "location.bankAccountNumber",
        ].join(","),
      },
      withCredentials: true,
      observe: "response",
    });
  }

  /**
   * Zgłoszenie zakupu przed wypchnięciem `purchase` do dataLayer.
   * `claimed: false` – zakup wysłał już serwer (GA4 MP) albo inna karta.
   */
  markPurchaseTracked(reservationId: string) {
    return this.http.post<{ claimed?: boolean }>(
      `/api/reservation/${reservationId}/purchase-tracked`,
      {},
      {
        params: { fields: "claimed" },
        withCredentials: true,
      },
    );
  }

  removeFromCart(data: any) {
    return this.http.delete(
      `/api/reservation/${data.reservation.id}/details/${data.itemId}`,
      {
        withCredentials: true,
        observe: "response",
      },
    );
  }

  removeAll(data: any) {
    return this.http.delete(`/api/reservation/${data.reservation.id}/details`, {
      withCredentials: true,
      observe: "response",
    });
  }

  createStorageContainer(data: any) {
    return this.http.post("/api/storageContainers", data, {
      withCredentials: true,
      observe: "response",
    });
  }

  saveStorageContainer(id: number, data: any) {
    return this.http.put(`/api/storageContainers/${id}`, data, {
      withCredentials: true,
      observe: "response",
    });
  }

  deleteStorageContainer(id: number) {
    return this.http.delete(`/api/storageContainers/${id}`, {
      withCredentials: true,
      observe: "response",
    });
  }

  updateStorageGroup(id: string, data: { invoiceLabel: string | null }) {
    return this.http.patch(`/api/storageContainers/groups/${id}`, data, {
      withCredentials: true,
      observe: "response",
    });
  }

  saveReservedContainer(reservationId: string, containers: any[]) {
    return this.http.patch(
      `/api/reservation/${reservationId}`,
      { storageContainers: containers },
      {
        withCredentials: true,
        observe: "response",
      },
    );
  }

  updateReservation(reservationId: string, data: any) {
    return this.http.patch(`/api/reservation/${reservationId}`, data, {
      withCredentials: true,
      observe: "response",
    });
  }

  updateLocation(locationId: string, data: any) {
    return this.http.patch(`/api/locations/${locationId}`, data, {
      withCredentials: true,
      observe: "response",
    });
  }

  /**
   * Kalendarz startów najmu lokalizacji (okno od dziś, blokady dni tygodnia
   * i zamknięć ustawione przez biuro). Bez zalogowania API zwraca tylko pola z `fields`, więc
   * kreator prosi o komplet potrzebny kalendarzowi; `params` nadpisuje domyślne
   * (panel dodaje `days`).
   */
  getStartCalendar(
    locationId: string,
    params: Record<string, string | number> = {
      fields: "minDate,maxDate,blockedDates",
    },
  ): Observable<StartCalendarResponse> {
    return this.http.get<StartCalendarResponse>(
      `/api/locations/${locationId}/start-calendar`,
      { params, withCredentials: true },
    );
  }

  /**
   * Podpowiedź świąt ustawowych z 12 miesięcy od dziś (panel). Same nie blokują
   * startu – biuro dodaje wybrane jako zamknięcie.
   */
  getUpcomingPublicHolidays(): Observable<PublicHoliday[]> {
    return this.http.get<PublicHoliday[]>("/api/locations/public-holidays", {
      withCredentials: true,
    });
  }

  /** Zamknięcia lokalizacji na starty najmu (najnowsze pierwsze). */
  getLocationStartBlackouts(locationId: string): Observable<StartBlackout[]> {
    return this.http.get<StartBlackout[]>(
      `/api/locations/${locationId}/start-blackouts`,
      { withCredentials: true },
    );
  }

  createLocationStartBlackout(
    locationId: string,
    data: Omit<StartBlackout, "id">,
  ): Observable<StartBlackout> {
    return this.http.post<StartBlackout>(
      `/api/locations/${locationId}/start-blackouts`,
      data,
      { withCredentials: true },
    );
  }

  updateLocationStartBlackout(
    locationId: string,
    blackoutId: string,
    data: Partial<Omit<StartBlackout, "id">>,
  ): Observable<StartBlackout> {
    return this.http.patch<StartBlackout>(
      `/api/locations/${locationId}/start-blackouts/${blackoutId}`,
      data,
      { withCredentials: true },
    );
  }

  deleteLocationStartBlackout(
    locationId: string,
    blackoutId: string,
  ): Observable<void> {
    return this.http.delete<void>(
      `/api/locations/${locationId}/start-blackouts/${blackoutId}`,
      { withCredentials: true },
    );
  }

  /** Rejestr bramy iGate placu: numery dodane na bramę + historia wysyłek. */
  getYardGateLog(yardId: string): Observable<YardGateLog> {
    return this.http.get<YardGateLog>(
      `/api/locations/yards/${yardId}/gate-log`,
      { withCredentials: true },
    );
  }

  /** Cennik lokalizacji: nowa pozycja – rodzaj kontenera z ceną netto i opcjonalnie promocyjną. */
  createLocationGroupPrice(
    locationId: string,
    data: {
      groupId: string;
      nettoUnitPrice: number;
      nettoUnitPromoPrice: number | null;
    },
  ) {
    return this.http.post(`/api/locations/${locationId}/storageGroups`, data, {
      withCredentials: true,
      observe: "response",
    });
  }

  /** Cennik lokalizacji: cena netto i promocyjna rodzaju kontenera (`null` usuwa promocję). */
  updateLocationGroupPrice(
    locationId: string,
    groupPriceId: string,
    data: { nettoUnitPrice: number; nettoUnitPromoPrice: number | null },
  ) {
    return this.http.patch(
      `/api/locations/${locationId}/storageGroups/${groupPriceId}`,
      data,
      {
        withCredentials: true,
        observe: "response",
      },
    );
  }

  deleteReservation(id: string) {
    return this.http.delete(`/api/reservation/${id}`, {
      withCredentials: true,
      observe: "response",
    });
  }

  createClient(data: any) {
    return this.http.post(`/api/clients`, data, {
      withCredentials: true,
      observe: "response",
    });
  }

  updateClient(clientId: string, data: any) {
    return this.http.patch(`/api/clients/${clientId}`, data, {
      withCredentials: true,
      observe: "response",
    });
  }

  deleteClient(id: any) {
    return this.http.delete(`/api/clients/${id}`, {
      withCredentials: true,
      observe: "response",
    });
  }

  createRent(data: any) {
    return this.http.post(`/api/rents`, data, {
      withCredentials: true,
      observe: "response",
    });
  }

  updateRent(rentId: string, data: any) {
    return this.http.patch(`/api/rents/${rentId}`, data, {
      withCredentials: true,
      observe: "response",
    });
  }

  createRentPayment(data: any) {
    return this.http.post("/api/rents/rentPayments", data, {
      withCredentials: true,
      observe: "response",
    });
  }

  updateRentPayment(rentPaymentId: string, data: any) {
    return this.http.patch(`/api/rents/rentPayments/${rentPaymentId}`, data, {
      withCredentials: true,
      observe: "response",
    });
  }

  deleteRentPayment(rentPaymentId: string) {
    return this.http.delete(`/api/rents/rentPayments/${rentPaymentId}`, {
      withCredentials: true,
      observe: "response",
    });
  }

  uploadFile(file: any) {
    const formData = new FormData();
    formData.append("file", file);
    return this.http.post("/api/files", formData, {
      withCredentials: true,
      observe: "response",
    });
  }

  sendProformaByEmail(reservationId: string, data: any) {
    return this.http.post(
      `/api/reservation/${reservationId}/invoice/email`,
      data,
      {
        withCredentials: true,
        observe: "response",
      },
    );
  }

  sendRentReminds(
    payload: { recipients: any[] },
  ): Observable<any> {
    return this.http.post<any>(
      "/api/rents/reminds",
      { ...payload },
    );
  }

  sendSms(data: any) {
    return this.http.post<any>(
      "/api/sms",
      data,
      {
        withCredentials: true,
        observe: "response",
      },
    );
  }

  public getMonthString(monthCount: number): string {
    const lastDigit = monthCount % 10;
    const penultimateDigit = monthCount % 100;

    if (monthCount === 1) {
      return `miesiąc`;
    } else if (
      lastDigit >= 2 &&
      lastDigit <= 4 &&
      (penultimateDigit < 10 || penultimateDigit >= 20)
    ) {
      return `miesiące`;
    } else {
      return `miesięcy`;
    }
  }

  /**
   * Klucz formy liczby mnogiej dla słowa „miesiąc" zależny od aktualnego języka
   * (common.months.one|few|many). Używać z pipe `translate`, żeby reagował na
   * zmianę języka: {{ ('common.months.' + getMonthPluralKey(n)) | translate }}.
   */
  public getMonthPluralKey(monthCount: number): "one" | "few" | "many" {
    const lang = this.translate.currentLang || this.translate.defaultLang || "pl";
    if (lang !== "pl") {
      return monthCount === 1 ? "one" : "few";
    }
    const lastDigit = monthCount % 10;
    const penultimateDigit = monthCount % 100;
    if (monthCount === 1) {
      return "one";
    }
    if (
      lastDigit >= 2 &&
      lastDigit <= 4 &&
      (penultimateDigit < 10 || penultimateDigit >= 20)
    ) {
      return "few";
    }
    return "many";
  }

  /**
   * Id rezerwacji po powrocie z AutoPay. Backend wydaje je tylko przy poprawnym
   * podpisie (Hash z parametrów powrotu), w innym przypadku `{ id: null }`.
   */
  getReservationFromPayment(params: AutopayReturnParams) {
    return this.http.get<{ id: string | null }>("/api/payments/order", {
      params: { ...params, fields: "id" },
      observe: "response",
    });
  }
}
